var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { styled } from 'styled-components';
import { Help } from '@hn-ui/icons';
import { Tooltip } from '@hn-ui/tooltips';
import pluralize from 'pluralize';
import { Table } from '../../../../../../Common/components/Table';
import { LabelByStudentStatus } from '../../../../../constants/statuses';
import { DetailsTableColumnWidth } from './tableConfig';
import { TaskCell } from './TaskCell';
import { useAppGoogleAnalytics } from '../../../../../../../../shared_app/hooks/useAppGoogleAnalytics';
import { ASSIGNMENTS_EVENTS, ASSIGNMENTS_EVENT_CATEGORIES, } from '../../../../../constants/analytics';
import { StruggleIcon } from '../../../../../../Common/components/StruggleIcon';
var Wrapper = styled.div.withConfig({ displayName: "Wrapper", componentId: "sc-1hbtw8s" })(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  padding: 24px 32px;\n  background-color: ", ";\n  box-shadow: inset 0px 4px 4px rgba(0, 0, 0, 0.03);\n\n  thead {\n    background-color: transparent;\n    box-shadow: none;\n  }\n\n  tr {\n    gap: 0 80px;\n  }\n\n  td {\n    padding: 4px 0;\n  }\n\n  row-gap: 12px;\n"], ["\n  padding: 24px 32px;\n  background-color: ", ";\n  box-shadow: inset 0px 4px 4px rgba(0, 0, 0, 0.03);\n\n  thead {\n    background-color: transparent;\n    box-shadow: none;\n  }\n\n  tr {\n    gap: 0 80px;\n  }\n\n  td {\n    padding: 4px 0;\n  }\n\n  row-gap: 12px;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.darkBlue99;
});
var CanceledCards = styled.div.withConfig({ displayName: "CanceledCards", componentId: "sc-1fc3nka" })(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: grid;\n  grid-template-columns: ", "px ", "px ", "px ", "px;\n  justify-items: flex-end;\n  row-gap: 16px;\n"], ["\n  display: grid;\n  grid-template-columns: ", "px ", "px ", "px ", "px;\n  justify-items: flex-end;\n  row-gap: 16px;\n"])), DetailsTableColumnWidth.TASK, DetailsTableColumnWidth.STATUS, DetailsTableColumnWidth.ACCURACY, DetailsTableColumnWidth.TIME);
var CanceledText = styled.p.withConfig({ displayName: "CanceledText", componentId: "sc-16a6vow" })(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: flex;\n  gap: 4px;\n  margin: 0;\n  color: ", ";\n  font-family: 'Noto Sans', sans-serif;\n  font-size: 16px;\n  font-weight: 400;\n  line-height: 24px;\n"], ["\n  display: flex;\n  gap: 4px;\n  margin: 0;\n  color: ", ";\n  font-family: 'Noto Sans', sans-serif;\n  font-size: 16px;\n  font-weight: 400;\n  line-height: 24px;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.black40;
});
var CanceledName = styled(CanceledText).withConfig({ displayName: "CanceledName", componentId: "sc-1y69va4" })(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  width: 100%;\n"], ["\n  width: 100%;\n"])));
var CanceledColumn = styled.p.withConfig({ displayName: "CanceledColumn", componentId: "sc-i89l94" })(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  margin: 0;\n  color: ", ";\n  font-size: 10px;\n  font-weight: 700;\n  line-height: 16px;\n  letter-spacing: 0.6px;\n  text-transform: uppercase;\n  width: 100%;\n"], ["\n  margin: 0;\n  color: ", ";\n  font-size: 10px;\n  font-weight: 700;\n  line-height: 16px;\n  letter-spacing: 0.6px;\n  text-transform: uppercase;\n  width: 100%;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.black30;
});
var CellWithStruggle = styled.p.withConfig({ displayName: "CellWithStruggle", componentId: "sc-nyrei7" })(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  color: ", ";\n  margin: 0;\n"], ["\n  color: ", ";\n  margin: 0;\n"])), function (_a) {
    var $struggled = _a.$struggled, theme = _a.theme;
    return $struggled ? theme.colors.red50 : theme.colors.black10;
});
var EmptyTooltip = function () { return (_jsx(Tooltip, __assign({ maxWidth: 390, content: 'These tasks were deleted from the platform by our content team and no longer available' }, { children: _jsx(Help, { color: 'black70' }, void 0) }), void 0)); };
var StudentDetailsTable = function (_a) {
    var student = _a.student, onLoad = _a.onLoad;
    var data = student.cards, deletedCards = student.deletedCards;
    var canceledCardsCount = (deletedCards === null || deletedCards === void 0 ? void 0 : deletedCards.cardsCount) || 0;
    var accuracy = (deletedCards === null || deletedCards === void 0 ? void 0 : deletedCards.accuracy) || 0;
    var timeSpent = (deletedCards === null || deletedCards === void 0 ? void 0 : deletedCards.timeSpent) || 0;
    var ga = useAppGoogleAnalytics();
    var handleCardLinkClick = React.useCallback(function () {
        ga === null || ga === void 0 ? void 0 : ga.sendEventAsTeacher({
            category: ASSIGNMENTS_EVENT_CATEGORIES.assignmentDetailsPage,
            action: ASSIGNMENTS_EVENTS.assign_report_page_playcard_click,
        });
    }, [ga]);
    var columns = React.useMemo(function () { return [
        {
            id: 'task',
            Header: 'Task',
            Cell: function (cellProps) { return (_jsx(TaskCell, __assign({}, cellProps, { onClick: handleCardLinkClick }), void 0)); },
            style: {
                minWidth: DetailsTableColumnWidth.TASK,
                maxWidth: DetailsTableColumnWidth.TASK,
                textAlign: 'left',
            },
        },
        {
            id: 'status',
            Header: 'Status',
            Cell: function (_a) {
                var original = _a.row.original;
                return LabelByStudentStatus[original.status];
            },
            style: {
                minWidth: DetailsTableColumnWidth.STATUS,
                maxWidth: DetailsTableColumnWidth.STATUS,
                textAlign: 'right',
            },
        },
        {
            id: 'time',
            Header: 'Time spent',
            Cell: function (_a) {
                var original = _a.row.original;
                return (_jsxs(CellWithStruggle, __assign({ "$struggled": original.struggledTime }, { children: [original.timeSpent, " min"] }), void 0));
            },
            style: {
                minWidth: DetailsTableColumnWidth.TIME,
                maxWidth: DetailsTableColumnWidth.TIME,
                textAlign: 'right',
            },
        },
        {
            id: 'accuracy',
            Header: 'Accuracy',
            Cell: function (_a) {
                var original = _a.row.original;
                return original.total ? (_jsxs(CellWithStruggle, __assign({ "$struggled": original.struggledAccuracy }, { children: [original.success, "/", original.total, " (", original.accuracy, "%)"] }), void 0)) : ('–');
            },
            style: {
                minWidth: DetailsTableColumnWidth.ACCURACY,
                maxWidth: DetailsTableColumnWidth.ACCURACY,
                textAlign: 'right',
            },
        },
        {
            id: 'struggle',
            Header: '',
            Cell: function (_a) {
                var _b = _a.row.original, struggledAccuracy = _b.struggledAccuracy, struggledTime = _b.struggledTime;
                return struggledAccuracy || struggledTime ? (_jsx(StruggleIcon, { struggledAccuracy: struggledAccuracy, struggledTime: struggledTime }, void 0)) : null;
            },
            style: {
                minWidth: DetailsTableColumnWidth.STRUGGLE,
                maxWidth: DetailsTableColumnWidth.STRUGGLE,
            },
        },
    ]; }, [handleCardLinkClick]);
    var hasCards = data.length > 0;
    React.useEffect(function () {
        onLoad === null || onLoad === void 0 ? void 0 : onLoad();
    }, [onLoad]);
    return (_jsxs(Wrapper, __assign({ "data-testid": 'assignment-student-details-table' }, { children: [hasCards && _jsx(Table, { data: data, columns: columns, isFlexLayout: false }, void 0), canceledCardsCount > 0 && (_jsxs(CanceledCards, { children: [!hasCards && (_jsxs(_Fragment, { children: [_jsx(CanceledColumn, { children: "task" }, void 0), _jsx(CanceledColumn, {}, void 0), _jsx(CanceledColumn, {}, void 0), _jsx(CanceledColumn, {}, void 0), _jsxs(CanceledName, { children: ["The tasks are no longer available", _jsx(EmptyTooltip, {}, void 0)] }, void 0)] }, void 0)), hasCards && (_jsxs(_Fragment, { children: [_jsxs(CanceledName, { children: ["and " + canceledCardsCount + " more " + pluralize('task', canceledCardsCount) + " (no longer available)", ' ', _jsx(EmptyTooltip, {}, void 0)] }, void 0), _jsx(CanceledText, {}, void 0), _jsx(CanceledText, { children: accuracy ? accuracy + " %" : '–' }, void 0), _jsxs(CanceledText, { children: [timeSpent, " min"] }, void 0)] }, void 0))] }, void 0))] }), void 0));
};
export { StudentDetailsTable };
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6;
